<template>
  <div style="position: relative;">
    <b-card
      v-if="
        !routeIsMessages &&
          Boolean(message.masked) &&
          (isTeacher || isCommentOwner(message))
      "
      class="w-100 h-100 card-overlay"
    >
      <b-card-text>
        <div class="text-center">
          <b-icon icon="eye-slash" font-scale="3"></b-icon>
        </div>
      </b-card-text>
    </b-card>
    <b-card
      class="message bg-transparent"
      :class="classObject"
      v-if="!routeIsMessages || isStudent"
    >
      <template v-slot:header>
        <b-btn
          v-if="
            Boolean(message.flagged) && (isCommentOwner(message) || isTeacher)
          "
          @click="openPropPanel('message')"
          variant="link"
          class="msg-warn-btn opacity-1"
          :disabled="isTeacher || dlibr.status == 0"
        >
          <b-icon icon="exclamation-circle-fill" variant="warning"></b-icon>
        </b-btn>
        <div class="d-flex justify-content-between">
          <span class="font-weight-bolder">
            {{ formatNameUser(message.user) }}
          </span>
          <small class="text-muted"
            >{{ message.created_date | formatDateTime }}
          </small>
        </div>
      </template>
      <b-card-text>
        <div
          class="text-center"
          v-if="
            Boolean(message.masked) && !isCommentOwner(message) && !isTeacher
          "
        >
          <small>
            <b-icon icon="eye-slash" font-scale="3" animation="fade"></b-icon>
            <p>Message masquée pour tous.</p>
          </small>
        </div>
        <div v-else>
          <p>
            <em> {{ messageText }}</em>
          </p>
        </div>
      </b-card-text>
      <template v-slot:footer>
        <div class="d-flex justify-content-between">
          <div>{{ typeMessage }}</div>
          <div>
            <b-btn
              v-if="canEdit"
              :disabled="isStudent && dlibr.status == 0"
              size="sm"
              variant="light"
              pill
              @click="openPropPanel('message')"
              v-b-tooltip.hover.up="'Modifier un message'"
            >
              <b-icon icon="pencil" variant="secondary"></b-icon>
            </b-btn>
            <b-btn
              v-if="
                isStudent &&
                  !isCommentOwner(message) &&
                  message.user.role != 'teacher'
              "
              @click="openPropPanelFlag('flag')"
              size="sm"
              variant="none"
              :disabled="Boolean(message.flagged) || dlibr.status == 0"
              v-b-tooltip.hover.top="'Signaler cette intervention'"
            >
              <b-icon
                icon="flag-fill"
                :variant="message.flagged ? 'warning' : 'primary'"
              ></b-icon>
            </b-btn>
            <b-btn
              v-if="isTeacher && !isCommentOwner(message) && flowDlibr.evalMode"
              v-b-tooltip.hover
              :title="
                Boolean(message.masked)
                  ? 'Démasquer le message'
                  : 'Masquer le message'
              "
              size="sm"
              variant="none"
              @click="show = !show"
            >
              <b-spinner
                class="text-secondary"
                small
                v-if="maskedLoading"
              ></b-spinner>
              <b-icon
                v-else
                @click="toggleMaskComment(message)"
                icon="eye-slash"
                :variant="Boolean(message.masked) ? 'danger' : 'primary'"
              ></b-icon>
              <!-- add tool tip -->
            </b-btn>
          </div>
        </div>
        <div class="text-center mt-2">
          <b-button
            v-if="answerMsgTeacher && !goMsg"
            variant="secondary"
            pill
            size="sm"
            @click="openPropPanel('MessageForm')"
            :disabled="dlibr.status == 0"
            >Répondre à l'enseignant</b-button
          >
          <b-button
            v-if="goMsg"
            variant="secondary"
            pill
            size="sm"
            @click="goToMsg()"
            >Voir la publication concerné</b-button
          >
        </div>
      </template>
    </b-card>
    <div v-else class="d-flex" :class="{ 'pl-4': Boolean(message.read) }">
      <b-button
        v-if="!Boolean(message.read)"
        class="custom-button px-2 mr-3"
        @click="onChangeRead()"
      ></b-button>
      <div>
        <div>
          <b-button
            variant="link"
            @click="onChangeRead()"
            class="font-weight-bolder text-primary"
            >{{ formatNameUser(message.user) }}</b-button
          >
        </div>
        <small class="text-muted">
          {{ message.created_date | formatDateTime }}
        </small>
        <div class="mt-1">
          {{ message.message }}
        </div>
      </div>
    </div>
  </div>
</template>
<script>
/**
 * @file       Comment message component.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */

import { commentsMixin } from "@/modules/comments/mixins";

/**
 * Export the component.
 * 
 * @type  {Object}
 */
export default {
  name: "MessageUnit",
  mixins: [commentsMixin],
  props: {
    comment: {
      type: Object,
      default: () => {},
      required: true
    },
    message: {
      type: Object,
      default: () => {},
      required: true
    },
    totalMessges: {
      type: Number
    },
    goMsg: {
      type: Boolean,
      default: false
    }
  },

  /**
   * Data used in the component.
   *
   * @return  {Object}
   */
  data() {
    return {
      maskedLoading: false
    };
  },

  /**
   * Computed properties.
   * 
   * @type  {Object}
   */
  computed: {
    /**
     * Type message.
     * 
     * @return   {String}   The type label
     */
    typeMessage() {
      return this.message.public === 1 ? "(public)" : "(privé)";
    },

    /**
     * Class object.
     * 
     * @return   {Object}   Message card booleans
     */
    classObject: function() {
      return {
        "card-type1 ": this.isCommentOwner(this.message),
        "card-type2 ": !this.isCommentOwner(this.message),
        "card-teacher": this.message.user.role === "teacher",
        "card-student": this.message.user.role === "student"
      };
    },

    /**
     * Type message.
     * 
     * @return   {String}   The type label
     */
    messageText() {
      return this.message.predefined_message
        ? this.message.predefined_message.message
        : this.message.message;
    },

    /**
     * Can edit, if the comment's owner.
     * 
     * @return   {Bool}   true or false
     */
    canEdit() {
      return (
        this.isCommentOwner(this.message)
      );
    },

    /**
     * Can answer message teacher.
     * 
     * @return   {Bool}   true or false
     */
    answerMsgTeacher() {
      return (
        this.isStudent &&
        this.isCommentOwner(this.comment) &&
        !this.isCommentOwner(this.message) &&
        this.message.read == 0
      );
    }
  },

  /**
   * Methods.
   * 
   * @type  {Object}
   */
  methods: {
    /**
     * Open prop panel.
     * 
     * @param   {Object}   mode       The mode
     */
    openPropPanel(mode) {
      this.$store.commit("deliberations/setPubToEdit", {
        pub: this.comment,
        mode
      });
      if (mode != "MessageForm") {
        this.message.isEdit = true;
      }
      this.$store.commit("comments/setMsgToEdit", this.message);
      mode !== "MessageForm"
        ? this.$root.$emit("bv::toggle::collapse", "property-panel")
        : this.$root.$emit("bv::toggle::collapse", "post-msg-sidebar");
    },

    /**
     * Open prop panel flag.
     * 
     * @param   {Object}   mode       The mode
     */
    openPropPanelFlag(mode) {
      this.$store.commit("deliberations/setPubToEdit", {
        pub: this.message,
        mode
      });
      this.$root.$emit("bv::toggle::collapse", "property-panel");
    },

    /**
     * Change read.
     */
    onChangeRead() {
      this.$store
        .dispatch("comments/putMessage", {
          message_id: this.message.id,
          read: 1
        })
        .then(() => {
          this.goToMsg();
        });
    },

    /**
     * Go to message.
     */
    goToMsg() {
      const root = {};
      if (this.message.comment.answer_to) {
        root.root = this.message.comment.answer_to;
      }
      this.$store
        .dispatch("comments/getComment", {
          deliberation: this.dlibr.id,
          id: this.message.comment.id,
          resourcesPerPage: 25,
          ...root
        })
        .then(() => {
          this.$store.commit("comments/setFlowDlibr", {
            reset: true,
            fields: {
              goToComment: {
                id: this.message.comment.id,
                resourcesPerPage: 25,
              }
            }
          });
          const params = {};
          params.teacher = this.dlibr.teacher;
          params.url = this.dlibr.url;
          if (this.message.comment.answer_to) {
            params.id = this.message.comment.answer_to;
          }
          this.$router.push({
            name: this.message.comment.answer_to
              ? "deliberation.byUrl.answers"
              : "deliberation.byUrl",
            params
          });
        });
    }
  }
};
</script>
<style lang="scss" scoped>
.card {
  margin-top: 0.5rem;
}
.message {
  .card-header {
    border-bottom: none;
    padding-bottom: 0.5rem !important;
    background-color: transparent !important;
    .msg-warn-btn {
      z-index: 1;
      position: absolute;
      top: 16px;
      right: -12px;
    }
  }
  .card-body {
    padding-bottom: 0;
    margin-bottom: -0.5rem;
    padding-top: 0;
  }
  .card-footer {
    background-color: transparent !important;
    padding-top: 0.1rem;
    border-top: none;
    z-index: 1;
  }
}

.card-overlay {
  background-color: white;
  position: absolute;
  border-radius: 20px;
  z-index: 1;
  opacity: 0.6;
  margin-top: 0;
  svg {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    color: #ececec;
  }
}

.card-teacher {
  background-color: #cccccc !important;
  font-size: 0.85rem !important;
}

.card-student {
  background-color: rgba(0, 0, 0, 0.03) !important;
  font-size: 0.85rem !important;
}

.card-type1 {
  border-radius: 20px;
}

.card-type2 {
  border-radius: 20px;
}

.custom-button {
  background-color: #51517b !important;
  border-radius: 0;
}

.opacity-1 {
  opacity: 1 !important;
}
</style>
